/**
 * i18n — key naming: `feature.section.element` (e.g. `nav.account.profile`, `header.search.placeholder`).
 *
 * Namespaces: `common`, `nav`, `tour`, `account`, `header`, `reviews`, `dashboard`, `auth`, `email`, `campaigns`, `popups`, `chats`, `shopify`, `wishlist`, `upgrade`, `help`, `public`, `errors`, `productTour`.
 * Locale bundles live in `src/locales/<lng>/index.ts`.
 *
 * Boot: only English ships in the main bundle. Other locales lazy-load on language change
 * (and at startup when a non-en preference is stored).
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import moment from "moment-timezone";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/ru";
import "moment/locale/it";
import "moment/locale/pt";
import "moment/locale/pt-br";
import "moment/locale/pl";
import "moment/locale/cs";
import "moment/locale/zh-cn";
import "moment/locale/zh-tw";
import "moment/locale/ja";
import "moment/locale/vi";
import "moment/locale/tr";
import "moment/locale/ko";
import "moment/locale/id";
import "moment/locale/sv";
import "moment/locale/nl";
import "moment/locale/uk";
import "moment/locale/hi";
import "moment/locale/lv";
import "moment/locale/da";
import { enResources } from "../locales/en";

export const EW_I18N_STORAGE_KEY = "ew_i18n_lang";

export const EW_I18N_LOCALES = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "ru", label: "Русский" },
    { code: "it", label: "Italiano" },
    { code: "hi", label: "हिन्दी" },
    { code: "bn", label: "বাংলা" },
    { code: "uk", label: "Українська" },
    { code: "pt-BR", label: "Português (Brasil)" },
    { code: "pt-PT", label: "Português (Portugal)" },
    { code: "pl", label: "Polski" },
    { code: "cs", label: "Čeština" },
    { code: "zh-CN", label: "中文（简体）" },
    { code: "zh-TW", label: "中文（繁體）" },
    { code: "ja", label: "日本語" },
    { code: "pa", label: "ਪੰਜਾਬੀ" },
    { code: "vi", label: "Tiếng Việt" },
    { code: "tr", label: "Türkçe" },
    { code: "ko", label: "한국어" },
    { code: "id", label: "Bahasa Indonesia" },
    { code: "sv", label: "Svenska" },
    { code: "da", label: "Dansk" },
    { code: "nl", label: "Nederlands" },
    { code: "lv", label: "Latviešu" },
    /** Pseudo-locale: English keys as values — for translators / extraction; not shown in header. */
    { code: "zz", label: "Template (EN)" },
] as const;

export type EwLocaleCode = (typeof EW_I18N_LOCALES)[number]["code"];

/** End-user languages only (excludes `zz` template locale). Use in header / compact pickers. */
export const EW_I18N_DISPLAY_LOCALES = EW_I18N_LOCALES.filter((l) => l.code !== "zz");

const DISPLAY_LOCALE_BY_LOWER = new Map(
    EW_I18N_DISPLAY_LOCALES.map((l) => [l.code.toLowerCase(), l.code] as const)
);

/** Map an active i18n tag to a registered display-locale code (empty when unknown). */
export function resolveEwDisplayLocaleCode(lng: string | undefined | null): EwLocaleCode | "" {
    if (!lng) {
        return "";
    }
    let tag = lng.trim();
    if (tag === "pt") {
        tag = "pt-BR";
    }
    if (tag === "in") {
        tag = "id";
    }
    const zh = tag.toLowerCase();
    if (zh === "zh" || zh === "zh-hans" || zh.startsWith("zh-hans-")) {
        tag = "zh-CN";
    } else if (zh === "zh-hant" || zh.startsWith("zh-hant-") || zh === "zh-hk" || zh === "zh-mo" || zh === "zh-tw") {
        tag = "zh-TW";
    }

    const lower = tag.toLowerCase();
    const exact = DISPLAY_LOCALE_BY_LOWER.get(lower);
    if (exact) {
        return exact;
    }

    const byLengthDesc = [...EW_I18N_DISPLAY_LOCALES].sort((a, b) => b.code.length - a.code.length);

    const byTagPrefix = byLengthDesc.find((l) => lower.startsWith(`${l.code.toLowerCase()}-`));
    if (byTagPrefix) {
        return byTagPrefix.code;
    }

    const byLocalePrefix = byLengthDesc.find((l) => l.code.toLowerCase().startsWith(`${lower}-`));
    if (byLocalePrefix) {
        return byLocalePrefix.code;
    }

    return "";
}

const SUPPORTED = new Set<string>(EW_I18N_LOCALES.map((l) => l.code));

function readStoredLanguage(): string {
    try {
        let v = localStorage.getItem(EW_I18N_STORAGE_KEY);
        if (v === "pt") v = "pt-BR"; /* legacy single “Portuguese” option */
        if (v && SUPPORTED.has(v)) return v;
    } catch {
        /* ignore */
    }
    return "en";
}

/** Maps UI locale codes to moment.js locale tags (relative times, formatted dates). */
export function ewLocaleToMomentLocale(lng: string): string {
    const m: Record<string, string> = {
        en: "en",
        es: "es",
        fr: "fr",
        de: "de",
        ru: "ru",
        it: "it",
        hi: "hi",
        bn: "en",
        uk: "uk",
        "pt-BR": "pt-br",
        "pt-PT": "pt",
        pl: "pl",
        cs: "cs",
        "zh-CN": "zh-cn",
        "zh-TW": "zh-tw",
        ja: "ja",
        pa: "en",
        vi: "vi",
        tr: "tr",
        ko: "ko",
        id: "id",
        sv: "sv",
        da: "da",
        nl: "nl",
        lv: "lv",
        zz: "en",
    };
    if (m[lng]) return m[lng];
    /* BCP 47: e.g. hi-IN → hi so moment matches; en-US → en for Latin digits */
    const primary = lng.split("-")[0];
    if (primary && m[primary]) return m[primary];
    return "en";
}

export function syncMomentLocale(lng: string) {
    moment.locale(ewLocaleToMomentLocale(lng || "en"));
}

function syncDocumentLanguage(lng: string) {
    if (typeof document !== "undefined") {
        document.documentElement.lang = lng || "en";
    }
}

type LocaleResources = Record<string, Record<string, unknown>>;

const LOCALE_LOADERS: Record<string, () => Promise<LocaleResources>> = {
    es: () => import("../locales/es").then((m) => m.esResources as LocaleResources),
    fr: () => import("../locales/fr").then((m) => m.frResources as LocaleResources),
    de: () => import("../locales/de").then((m) => m.deResources as LocaleResources),
    ru: () => import("../locales/ru").then((m) => m.ruResources as LocaleResources),
    it: () => import("../locales/it").then((m) => m.itResources as LocaleResources),
    hi: () => import("../locales/hi").then((m) => m.hiResources as LocaleResources),
    bn: () => import("../locales/bn").then((m) => m.bnResources as LocaleResources),
    uk: () => import("../locales/uk").then((m) => m.ukResources as LocaleResources),
    "pt-BR": () => import("../locales/pt-BR").then((m) => m.ptBRResources as LocaleResources),
    "pt-PT": () => import("../locales/pt-PT").then((m) => m.ptPTResources as LocaleResources),
    pl: () => import("../locales/pl").then((m) => m.plResources as LocaleResources),
    cs: () => import("../locales/cs").then((m) => m.csResources as LocaleResources),
    "zh-CN": () => import("../locales/zh-CN").then((m) => m.zhCNResources as LocaleResources),
    "zh-TW": () => import("../locales/zh-TW").then((m) => m.zhTWResources as LocaleResources),
    ja: () => import("../locales/ja").then((m) => m.jaResources as LocaleResources),
    pa: () => import("../locales/pa").then((m) => m.paResources as LocaleResources),
    vi: () => import("../locales/vi").then((m) => m.viResources as LocaleResources),
    tr: () => import("../locales/tr").then((m) => m.trResources as LocaleResources),
    ko: () => import("../locales/ko").then((m) => m.koResources as LocaleResources),
    id: () => import("../locales/id").then((m) => m.idResources as LocaleResources),
    sv: () => import("../locales/sv").then((m) => m.svResources as LocaleResources),
    da: () => import("../locales/da").then((m) => m.daResources as LocaleResources),
    nl: () => import("../locales/nl").then((m) => m.nlResources as LocaleResources),
    lv: () => import("../locales/lv").then((m) => m.lvResources as LocaleResources),
    zz: () => import("../locales/zz").then((m) => m.zzResources as LocaleResources),
};

const localeLoadPromises = new Map<string, Promise<void>>();

function addLocaleBundles(lng: string, resources: LocaleResources): void {
    Object.keys(resources).forEach((ns) => {
        i18n.addResourceBundle(lng, ns, resources[ns], true, true);
    });
}

/** Ensure locale JSON is loaded before switching language. Safe to call repeatedly. */
export function ensureLocaleLoaded(lng: string): Promise<void> {
    if (!lng || lng === "en" || i18n.hasResourceBundle(lng, "common")) {
        return Promise.resolve();
    }
    const existing = localeLoadPromises.get(lng);
    if (existing) {
        return existing;
    }
    const loader = LOCALE_LOADERS[lng];
    if (!loader) {
        return Promise.resolve();
    }
    const promise = loader()
        .then((resources) => {
            addLocaleBundles(lng, resources);
        })
        .catch((err) => {
            localeLoadPromises.delete(lng);
            throw err;
        });
    localeLoadPromises.set(lng, promise);
    return promise;
}

const NS = [
    "common",
    "nav",
    "tour",
    "account",
    "header",
    "reviews",
    "dashboard",
    "auth",
    "email",
    "campaigns",
    "popups",
    "chats",
    "shopify",
    "wishlist",
    "upgrade",
    "help",
    "public",
    "errors",
    "productTour",
] as const;

i18n.use(initReactI18next).init({
    /**
     * i18next 21 `nonExplicitSupportedLngs` strips `zh-CN` → `zh` before the
     * supportedLngs check, so Chinese / Portuguese never enter the resolve
     * hierarchy and every string falls back to English. Map tags ourselves
     * via `resolveEwDisplayLocaleCode` instead.
     */
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    partialBundledLanguages: true,
    resources: {
        en: enResources,
    },
    lng: "en",
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED],
    defaultNS: "common",
    ns: [...NS],
    interpolation: {
        escapeValue: false,
    },
    react: {
        useSuspense: false,
    },
});

syncMomentLocale(i18n.language);
syncDocumentLanguage(i18n.language);
i18n.on("languageChanged", (lng) => {
    void ensureLocaleLoaded(lng).then(() => {
        syncMomentLocale(lng);
        syncDocumentLanguage(lng);
    });
});

const preferred = readStoredLanguage();
if (preferred !== "en") {
    void ensureLocaleLoaded(preferred).then(() => {
        void i18n.changeLanguage(preferred);
    });
}

export default i18n;
